'use client';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { BULLETIN_CODE_REGEX, CNP_PATTERN } from 'lib/constants';
import { fetchJson } from 'lib/fetch-json';
import { Trans } from 'src/i18n/Trans';
import { useTranslation } from 'src/i18n/client';
import Button from 'src/components/Button';
import FormError from 'src/components/FormError';
import toast from 'src/components/TailwindToaster';
// import useRecaptcha from 'src/hooks/useRecaptcha';
import { bulletinResultUrl } from 'src/services/route-utils';
import CNPDisclosure from './CNPDisclosure';

const PASSCODE_LENGTH = 6;

type Props = {
    initialData?: {
        bulletin?: string;
        access?: string;
    };
};

export default function PageForm({ initialData }: Props) {
    const { t } = useTranslation(null, 'common');
    // const { isReady, generateToken } = useRecaptcha();
    const router = useRouter();
    const {
        control,
        formState: { errors: _errors },
        handleSubmit,
    } = useForm({
        defaultValues: {
            bulletin: initialData?.bulletin || '',
            access: initialData?.access || '',
            cnp: '',
            terms: '',
        },
    });
    const [loading, setLoading] = useState(false);
    const errors: any = _errors;

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);

            // const recaptcha = await generateToken();
            const body = {
                // recaptcha,
                bulletin: data.bulletin,
                cnp: data.cnp,
                access: data.access,
            };

            const response: any = await fetchJson('/json/results/view', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            router.push(bulletinResultUrl(response.data.slug));
        } catch (error: any) {
            if (error?.data?.error?.code === 400010) {
                toast.error(t('bulletinResults.cnpNotMatching'));
            } else {
                toast.error(t('bulletinResults.error'));
            }

            setLoading(false);
        }
    };

    return (
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="px-4 text-center md:w-2/3 lg:w-1/2">
            <h1 className="mt-4 text-5xl md:mt-8 lg:text-6xl">{t('bulletinResults.title')}</h1>

            <p className="mb-10 mt-4 text-sm">{t('bulletinResults.info')}</p>

            <div className="mx-auto mb-4 md:mb-8 xl:w-2/3">
                <div className="my-4">
                    <Controller
                        control={control}
                        name="bulletin"
                        rules={{
                            required: t('bulletinResults.bulletinRequired'),
                            pattern: {
                                value: BULLETIN_CODE_REGEX,
                                message: t('bulletinResults.bulletinPattern'),
                            },
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                            <input
                                ref={ref}
                                className={errors.bulletinNo ? 'is-invalid' : ''}
                                placeholder={t('bulletinResults.bulletin')}
                                onChange={(event) => onChange(event.target.value?.toUpperCase())}
                                value={value || ''}
                            />
                        )}
                    />
                    <FormError error={errors.bulletin} />
                </div>
                <div className="my-4">
                    <Controller
                        control={control}
                        name="access"
                        rules={{
                            required: t('bulletinResults.accessRequired'),
                            minLength: {
                                value: PASSCODE_LENGTH,
                                message: t('bulletinResults.accessLength', { length: PASSCODE_LENGTH }),
                            },
                            maxLength: {
                                value: PASSCODE_LENGTH,
                                message: t('bulletinResults.accessLength', { length: PASSCODE_LENGTH }),
                            },
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                            <input
                                ref={ref}
                                className={`no-arrows ${errors.passcode ? 'is-invalid' : ''}`}
                                placeholder={t('bulletinResults.access')}
                                onChange={(event) => onChange(event.target.value)}
                                type="number"
                                value={value || ''}
                            />
                        )}
                    />
                    <FormError error={errors.access} />
                </div>

                <CNPDisclosure />
                <div className="my-4">
                    <Controller
                        control={control}
                        name="cnp"
                        rules={{
                            pattern: {
                                value: CNP_PATTERN,
                                message: t('bulletinResults.cnpPattern'),
                            },
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                            <input
                                ref={ref}
                                className={`no-arrows ${errors.passcode ? 'is-invalid' : ''}`}
                                placeholder={t('bulletinResults.cnp')}
                                onChange={(event) => onChange(event.target.value)}
                                type="number"
                                value={value || ''}
                            />
                        )}
                    />
                    <FormError error={errors.cnp} />
                </div>

                <div className="mb-3 text-left">
                    <label className="flex items-start gap-2">
                        <Controller
                            control={control}
                            name="terms"
                            rules={{ required: t('activate.termsRequired') }}
                            render={({ field: { onChange } }) => (
                                <input className="mt-1" type="checkbox" disabled={loading} onChange={onChange} />
                            )}
                        />
                        <span>
                            <Trans
                                t={t}
                                i18nKey="common:activate.termsLabel"
                                components={{
                                    link: (
                                        <Link
                                            className="text-primary underline hover:no-underline"
                                            href="/termeni-si-conditii"
                                        />
                                    ),
                                }}
                            />
                        </span>
                    </label>
                    <FormError error={errors.terms} />
                </div>

                <Button
                    type="submit"
                    variant="primary"
                    disabled={loading} //!isReady
                    loading={loading}
                    className="mt-10 w-full">
                    {t('bulletinResults.label')}
                </Button>
            </div>
        </form>
    );
}
